import type { LevelGameSnapshot } from "@/core/LevelGame";
import { CURRENT_SCORING_VERSION } from "@/online/types";
import type { Platform } from "@/platform/Platform";
import { ENDLESS_RULES_VERSION, type EndlessEndReason, type EndlessSubmissionMetrics } from "./types";

/**
 * Everything `buildEndlessSubmissionMetrics()` needs, gathered by the
 * caller (`GameScene.finishEndlessRun()`) from `RunTracker` and the
 * engine's own `LevelGameSnapshot` - kept as plain data, never a
 * `RunTracker`/`LevelGame` instance, so this builder stays a pure function
 * with no framework/class coupling and is trivially unit-testable.
 */
export interface EndlessSubmissionMetricsInput {
  runId: string;
  snapshot: LevelGameSnapshot;
  aggregateCounters: {
    jackpotCount: number;
    wormholeCount: number;
    shrinkCount: number;
    speedTrialStartedCount: number;
    speedTrialSurvivedCount: number;
    scoreHitCount: number;
    pauseCount: number;
    pausedDurationMs: number;
  };
  safeCount: number;
  rushCount: number;
  chaosCount: number;
  maxRushCombo: number;
  endReason: EndlessEndReason;
  deathReason: "wall" | "obstacle" | "self" | null;
  survivalTimeMs: number;
  fastestStepMs: number;
  /** PR34: the run's frozen platform - see `EndlessSubmissionMetrics.platform`. */
  platform?: Platform;
}

/**
 * Builds the Endless-specific submission/proof shape for the global
 * leaderboard - a small, standalone, mode-neutral builder, deliberately
 * never calling Campaign's `RunTracker.buildSubmission()` (which requires
 * a level/world id, stars, and a completion reason with no Endless
 * equivalent - see `RunSubmission`'s own doc). `finalScore`/`biteScore`/
 * `skillScore` are read straight off the snapshot the HUD/result screen
 * already use (`finalScoreSoFar = score + skillScore`), never a separate
 * Endless-only scoring formula.
 */
export function buildEndlessSubmissionMetrics(input: EndlessSubmissionMetricsInput): EndlessSubmissionMetrics {
  const { snapshot, aggregateCounters } = input;
  return {
    runId: input.runId,
    endlessRulesVersion: ENDLESS_RULES_VERSION,
    scoringVersion: CURRENT_SCORING_VERSION,
    endReason: input.endReason,
    deathReason: input.deathReason,
    finalLength: snapshot.length,
    finalScore: snapshot.finalScoreSoFar,
    survivalTimeMs: input.survivalTimeMs,
    biteScore: snapshot.score,
    skillScore: snapshot.skillScore,
    safeCount: input.safeCount,
    rushCount: input.rushCount,
    chaosCount: input.chaosCount,
    jackpotCount: aggregateCounters.jackpotCount,
    wormholeCount: aggregateCounters.wormholeCount,
    shrinkCount: aggregateCounters.shrinkCount,
    speedTrialStartedCount: aggregateCounters.speedTrialStartedCount,
    speedTrialSurvivedCount: aggregateCounters.speedTrialSurvivedCount,
    scoreHitCount: aggregateCounters.scoreHitCount,
    maxRushCombo: input.maxRushCombo,
    // The Speed Engine's interpolated step interval is a genuine float
    // (SpeedEngine.currentStepMs - a linear interpolation toward a
    // fractional growth/RUSH-debt-derived target, e.g. 124.06015037593984)
    // - never rounded anywhere upstream, because until this submission
    // shape existed nothing ever persisted a raw millisecond interval
    // (score/skillScore are already integers at the point they're awarded
    // - see ManeuverTracker's own Math.round(); survivalTimeMs/
    // pausedDurationMs are already rounded by RunTracker). Round to the
    // nearest integer HERE, at the one canonical submission boundary,
    // before this value is ever queued or JSON-serialized - the gameplay
    // Speed Engine itself, and the permanent 75ms floor it enforces, are
    // completely untouched by this. Every valid interpolated value stays
    // >= the 75ms floor (SpeedEngine.advance() only ever interpolates
    // between two already-cap-clamped endpoints), so rounding can never
    // produce a value below it either.
    fastestStepMs: Math.round(input.fastestStepMs),
    moveCount: snapshot.moveCount,
    perfectFoldCount: snapshot.maneuverCounts.perfect_fold,
    lastSecondTurnCount: snapshot.maneuverCounts.last_second_turn,
    tailGrazeCount: snapshot.maneuverCounts.tail_graze,
    threadTheNeedleCount: snapshot.maneuverCounts.thread_the_needle,
    doubleDangerCount: snapshot.doubleDangerCount,
    bestFlowStreak: snapshot.bestFlowStreak,
    trickBiteCount: snapshot.trickBiteCount,
    clutchBiteCount: snapshot.clutchBiteCount,
    pauseCount: aggregateCounters.pauseCount,
    pausedDurationMs: aggregateCounters.pausedDurationMs,
    ...(input.platform ? { platform: input.platform } : {}),
  };
}
